import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import useV3Offers from './useV3Offers';
import useV3Budgets from './useV3Budgets';
import useV3LineupActions from './useV3LineupActions';
import useV3OfferActions from './useV3OfferActions';
import useV3OfferBudget, { formatCalculatedNumber } from './useV3OfferBudget';
import useV3OfferSend from './useV3OfferSend';
import useIsMobile from '../../shared/hooks/useIsMobile';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import useEventTemplates from './useEventTemplates';
import { parseFloatFromString } from '../utils/parseFloatFromString';
import { isPoolPreviewActive, activeMemberSettlementDue } from '../utils/buildPoolEngineInput';
import { POOL_SHARE_FIELDS, POOL_MEMBER_FIELDS, restorablePoolShare, restorableMemberFields, poolShareConfirmed } from '../utils/poolShareRollback';
import buildHoldDatesText from '../utils/buildHoldDatesText';
import { collectOfferPerformers } from '../utils/offerPerformers';
import { copyText } from '@opendate-app/setlist';

export default function useV3OfferEditController() {
  const {
    csrfToken,
    team,
    currentUser,
    offer,
    budget,
    lineItems,
    isSaving,
    eventOffers,
    confirm,
    entryOrigin,
    changelogVersions,
    lastEditedAt,
    editorVariant,
    dispatch,
  } = useV3OfferContext();

  const {
    handlePreview,
    handleCopy,
    copyModalOpen,
    isCopying,
    handleSubmitCopy,
    handleCancelCopy,
    handleDownload,
    handleDelete,
    handleChangeActType,
    handleCreateHold,
    handleEditPrimaryPerformer,
  } = useV3OfferActions();

  const canCopyHolds = confirm?.type === 'Hold'
    && (confirm.hold_positions || []).length > 0;

  const handleCopyHolds = useCallback(() => {
    if (!canCopyHolds) {
      return;
    }
    const venueName = offer?.venue?.name || confirm?.venue?.name || null;
    const tz = confirm?.venue_ownership?.tzinfo_identifier || null;
    const text = buildHoldDatesText(
      confirm.hold_positions,
      venueName,
      tz,
      team?.append_availability_disclaimer_to_hold_dates,
    );
    if (!text) {
      return;
    }
    copyText(text).then((ok) => {
      if (ok) toastSuccess('Copied hold dates to clipboard');
    });
  }, [
    canCopyHolds,
    offer?.venue?.name,
    confirm?.venue?.name,
    confirm?.venue_ownership?.tzinfo_identifier,
    confirm?.hold_positions,
    team?.append_availability_disclaimer_to_hold_dates,
  ]);

  const { updateOffer } = useV3Offers();
  const {
    reorderLineItem,
    createBudgetBonus: apiCreateBudgetBonus,
    updateBudgetBonus: apiUpdateBudgetBonus,
    deleteBudgetBonus: apiDeleteBudgetBonus,
  } = useV3Budgets();

  const {
    budgetForComponents,
    siblingGuaranteeExpenses,
    budgetForMath,
    calculated,
    eventVenueNet,
    hasSecondaryCurrencySymbol,
    globalCurrency,
    showCurrencyConversion,
    handleUpdateBudget,
    handleAutoSaveLineItem,
    handleDeleteLineItem,
    handleCreateTicketLineItem,
    handleCreateIncomeLineItem,
    handleCreateAdditionLineItem,
    handleCreateExpenseLineItem,
    handleCreateDeductionLineItem,
    handleCreateBeforeAdjustedDeductionsLineItem,
    handleCreateAncillaryIncomeLineItem,
    handleCreateAncillaryExpenseLineItem,
  } = useV3OfferBudget();

  // Persist which editor (full V3 vs simple) the user actually saves in, so the
  // offer reloads into that UI later (O-3412). Fired lazily on the first save of
  // any kind — and only when it differs from what's stored — so merely viewing a
  // page doesn't change the preference, and re-saving in the same UI is a no-op.
  // Guarded per offer id so switching to a sibling offer records that one too.
  const recordedVariantOfferIdRef = useRef(null);
  const recordEditorVariant = useCallback(() => {
    if (!editorVariant || !offer?.id) return;
    if (offer.editor_variant === editorVariant) return;
    if (recordedVariantOfferIdRef.current === offer.id) return;
    recordedVariantOfferIdRef.current = offer.id;
    // Fire-and-forget, and deliberately NOT via updateOffer: it dispatches
    // SET_OFFER, and that response (carrying pre-change field values) could race
    // the user's actual save and momentarily revert a field in local state.
    // This only writes the column; offer.editor_variant stays stale this session
    // (the ref guard prevents re-firing) and the DB value is read on next load.
    axios.patch(
      `/teams/${team.id}/offers/v3/${offer.id}`,
      { v3_offer: { editor_variant: editorVariant } },
      { headers: { 'X-CSRF-TOKEN': csrfToken } },
    ).catch(() => {});
  }, [editorVariant, offer?.id, offer?.editor_variant, team?.id, csrfToken]);

  // Local only. Marking a row already syncs apply_tax_after_facility_fee onto
  // every offer on the budget server-side; PATCHing it again from here would race
  // the in-flight deal-terms autosave and could revert it. (O-4778)
  const handlePassThroughChange = useCallback((on) => {
    dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { apply_tax_after_facility_fee: on } });
  }, [dispatch]);

  const updateOfferRecordingVariant = useCallback((offerId, offerParams, options) => {
    recordEditorVariant();
    return updateOffer(offerId, offerParams, options);
  }, [recordEditorVariant, updateOffer]);

  const handleAutoSaveLineItemRecordingVariant = useCallback((...args) => {
    recordEditorVariant();
    return handleAutoSaveLineItem(...args);
  }, [recordEditorVariant, handleAutoSaveLineItem]);

  const handleUpdateBudgetRecordingVariant = useCallback((...args) => {
    recordEditorVariant();
    return handleUpdateBudget(...args);
  }, [recordEditorVariant, handleUpdateBudget]);

  // Modifier fields (Tax Withholding, Merchandising, Promoter Profit) bind their
  // inputs to offer fields. Update local state optimistically so the controlled
  // input reflects typing immediately, then DEBOUNCE the PATCH: persisting on
  // every keystroke fired racing requests whose out-of-order responses (each a
  // full SET_OFFER) clobbered the field — e.g. typing "20" reverted to "2".
  //
  // The buffer is keyed by offer. In Pool mode this path also carries the
  // per-member settlement columns (pool_payout_strategy, pool_percentage), and
  // switching artist inside the 400ms window used to fold the outstanding
  // fields into the NEXT member's payload — paying artist B on artist A's
  // share. Changing target flushes what is owed to its own offer first.
  const modifierFieldTimerRef = useRef(null);
  const modifierFieldPendingRef = useRef({ offerId: null, fields: {} });

  // The context offer as of the latest render, for the async rollback below —
  // the callback's own closure is a render old by the time a PATCH answers.
  const contextOfferRef = useRef(offer);
  contextOfferRef.current = offer;

  // In Pool mode this path persists the settlement columns, and `updateOffer`
  // only toasts on a refusal — so the optimistic dispatch would stand and the
  // split card would keep showing a split the server rejected, with a booker
  // reasonably reading that as saved. Snapshot the pre-edit pair once per edit
  // window (per offer) and put it back if the save is refused. The complex
  // editor does the same in useV3OfferAutosave; the rule itself is shared.
  const poolShareRollbackRef = useRef(null);

  // O-4755. Pre-edit values for the OTHER modifier fields in a batch (tax
  // withholding, merchandising, promoter profit). The settlement columns above
  // move as a group with their own rules; these are independent of each other,
  // so they snapshot and restore per field.
  const modifierRollbackRef = useRef({ offerId: null, fields: {} });

  // A fired save's await outlives its timer, so clearing the timeout on unmount
  // only cancels a save that has NOT gone out yet. This flag closes the rest.
  const modifierMountedRef = useRef(true);
  useEffect(() => () => {
    modifierMountedRef.current = false;
    if (modifierFieldTimerRef.current) clearTimeout(modifierFieldTimerRef.current);
  }, []);

  const savePoolAwareOffer = useCallback((offerId, fields, options) => {
    const touchesPoolShare = POOL_MEMBER_FIELDS.some((f) => f in fields);
    const rollback = poolShareRollbackRef.current;

    // The plain fields this batch carried, and their pre-edit values. Restoring
    // the whole snapshot would revert a field the batch never sent — a refused
    // cap taking a floor typed moments earlier with it.
    const plain = modifierRollbackRef.current;
    const plainBatch = {};
    Object.keys(fields)
      .filter((f) => !POOL_MEMBER_FIELDS.includes(f))
      .forEach((f) => { if (f in plain.fields) plainBatch[f] = plain.fields[f]; });

    return updateOfferRecordingVariant(offerId, fields, options).then((saved) => {
      if (!modifierMountedRef.current) return saved;

      // Re-seed each saved plain key from the value the server confirmed rather
      // than dropping it: a window opened while this save was in flight would
      // otherwise roll back against nothing and leave its refused value on
      // screen. The confirmed value is the right baseline for whatever that
      // window changes next.
      if (saved && modifierRollbackRef.current === plain && plain.offerId === offerId) {
        Object.keys(plainBatch).forEach((f) => { plain.fields[f] = saved[f] ?? null; });
      }
      if (!saved && (contextOfferRef.current?.id ?? null) === offerId
          && Object.keys(plainBatch).length > 0) {
        dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: plainBatch });
      }

      if (!touchesPoolShare || !rollback || rollback.offerId !== offerId) return saved;

      if (saved) {
        // Only what this response confirms; a keystroke that landed mid-flight
        // still needs its snapshot.
        if (poolShareConfirmed({ current: contextOfferRef.current, confirmed: saved })) {
          poolShareRollbackRef.current = null;
        }
        return saved;
      }

      // Clear only if this is still the snapshot we captured. A second save armed
      // while this one was in flight installed its own, and nulling the ref
      // blindly would leave that one's refusal with nothing to put back.
      if (poolShareRollbackRef.current === rollback) poolShareRollbackRef.current = null;
      // Only roll back the offer still on screen: a refusal that lands after
      // the booker has moved on must not write this member's columns onto the
      // one they are looking at.
      if ((contextOfferRef.current?.id ?? null) !== offerId) return saved;

      const args = { previous: rollback.previous, sent: fields, current: contextOfferRef.current };
      const restore = {
        ...(restorableMemberFields(args) || {}),
        ...(restorablePoolShare(args) || {}),
      };
      if (Object.keys(restore).length > 0) {
        dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: restore });
      }
      return saved;
    });
  }, [dispatch, updateOfferRecordingVariant]);

  // Send any buffered modifier edit NOW rather than 400ms from now. Clicking
  // Save deal within the debounce window otherwise put two PATCHes in flight for
  // the same offer, and the deal response — which carries the pre-edit pool
  // columns — could land second and revert them through SET_OFFER.
  const flushModifierFields = useCallback(() => {
    if (modifierFieldTimerRef.current) {
      clearTimeout(modifierFieldTimerRef.current);
      modifierFieldTimerRef.current = null;
    }
    const { offerId, fields } = modifierFieldPendingRef.current;
    if (!offerId || Object.keys(fields).length === 0) return Promise.resolve(null);

    modifierFieldPendingRef.current = { offerId: null, fields: {} };
    return savePoolAwareOffer(offerId, fields);
  }, [savePoolAwareOffer]);

  const handleChangeModifierField = useCallback((field, value) => {
    // Everything below reads the LATEST offer through the ref: this callback is
    // memoized, so its own `offer` closure freezes at mount.
    const currentOfferId = contextOfferRef.current?.id ?? null;
    if (!POOL_MEMBER_FIELDS.includes(field)) {
      // First write per field wins: the snapshot holds the value from before
      // this window opened, not the previous keystroke.
      const plain = modifierRollbackRef.current;
      if (plain.offerId !== currentOfferId) {
        modifierRollbackRef.current = { offerId: currentOfferId, fields: {} };
      }
      const plainSnapshot = modifierRollbackRef.current;
      if (!(field in plainSnapshot.fields)) {
        plainSnapshot.fields[field] = contextOfferRef.current?.[field] ?? null;
      }
    }

    if (POOL_MEMBER_FIELDS.includes(field)) {
      const snapshot = poolShareRollbackRef.current;
      if (!snapshot || snapshot.offerId !== currentOfferId) {
        poolShareRollbackRef.current = {
          offerId: currentOfferId,
          // From the ref: a snapshot taken off the frozen closure restores
          // mount-time values rather than what the server actually holds.
          previous: Object.fromEntries(
            POOL_MEMBER_FIELDS.map((f) => [f, contextOfferRef.current?.[f] ?? null]),
          ),
        };
      }
    }

    dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { [field]: value } });

    // The settlement columns travel together or the server reads one against the
    // wrong base — a lone 55 meant "of the remainder" lands on whatever strategy
    // the row still has and becomes 55% of the whole pool, accepted silently
    // because that pair is legal.
    //
    // Captured HERE, from the offer being edited, not at fire time from
    // `contextOfferRef`. That ref is wholesale-replaced by every SET_OFFER —
    // including the simple editor's own Save, whose response carries the
    // pre-edit settlement columns — so completing the pair 400ms later could
    // pair a fresh share with a rewound strategy. The keystroke is the only
    // moment both values are known to belong together.
    const pending = modifierFieldPendingRef.current;

    const edit = { [field]: value };
    if (POOL_SHARE_FIELDS.includes(field)) {
      // The sibling column comes from this window's own buffer FIRST, then from
      // the LATEST offer — via the ref, never the closure.
      //
      // Two separate staleness traps meet here:
      //   1. This callback is memoized on `offer?.id`, and everything else in
      //      its dep list is stable, so the `offer` it closes over freezes at
      //      mount and never sees an edit. Reading it after the 400ms buffer
      //      flushed re-asserted mount-time state.
      //   2. Within one handler the reducer REPLACES the offer object, so two
      //      synchronous writes — which is exactly how picking a strategy works,
      //      strategy then seeded share — cannot see each other at all.
      // Either one alone persisted `explicit @ 100%`: a share of the WHOLE pool,
      // one member taking everything and the rest at $0, accepted silently
      // because that pair is legal. The buffer covers (2), the ref covers (1).
      const current = contextOfferRef.current;
      const buffered = pending.offerId === (current?.id ?? null) ? pending.fields : {};
      const known = { ...(current || {}), ...buffered };
      POOL_SHARE_FIELDS.forEach((f) => {
        if (!(f in edit)) edit[f] = known[f] ?? null;
      });
    }

    const switchedOffer = pending.offerId !== null && pending.offerId !== currentOfferId;
    if (modifierFieldTimerRef.current) clearTimeout(modifierFieldTimerRef.current);
    if (switchedOffer && Object.keys(pending.fields).length) {
      // The guard above guarantees this is NOT the offer on screen, so the
      // response must not be echoed into the context: SET_OFFER is a wholesale
      // replace, and it would repaint the editor on the previous artist.
      savePoolAwareOffer(pending.offerId, pending.fields, { syncContext: false });
    }

    modifierFieldPendingRef.current = {
      offerId: currentOfferId,
      fields: switchedOffer ? edit : { ...pending.fields, ...edit },
    };
    modifierFieldTimerRef.current = setTimeout(() => {
      const { offerId, fields } = modifierFieldPendingRef.current;
      modifierFieldPendingRef.current = { offerId: null, fields: {} };
      savePoolAwareOffer(offerId, fields);
    }, 400);
    // No `offer` in the dep list — every read goes through contextOfferRef,
    // so a stable callback is correct here rather than a hazard.
  }, [dispatch, savePoolAwareOffer]);

  // The Simple deal form writes deposits and bonuses straight to the server
  // (V3BudgetDeposits/V3BudgetBonuses controllers), bypassing the budget context
  // slice. Re-fetch the offer's budget children after that save so reopening the
  // form reseeds from fresh data — otherwise just-saved rows look lost until a
  // full page reload. (Create mode already refreshes via handleSelectOffer.)
  const refreshBudgetChildren = useCallback(async () => {
    if (!offer?.id) return;
    try {
      const { data } = await axios.get(`/teams/${team.id}/offers/v3/${offer.id}.json`);
      const newBudget = data?.v3_budget;
      if (!newBudget) return;
      dispatch({
        type: V3_ACTIONS.UPDATE_BUDGET,
        payload: {
          v3_budget_deposits: newBudget.v3_budget_deposits || [],
          v3_budget_bonuses: newBudget.v3_budget_bonuses || [],
        },
      });
    } catch (e) {
      // Non-fatal: the rows already persisted; context just stays stale until reload.
    }
  }, [team?.id, offer?.id, dispatch]);

  // O-2783: in Pool mode the active artist's "Potential Artist Earnings" is
  // their individual (post-tax) pool take from calculated.poolMembers, keyed by
  // performerId — NOT calculated.artistEarnings.artistSettlementDue, which the
  // engine overwrites with the SUM across all members. In Independent mode this
  // is the unchanged single-offer value.
  const performers = useMemo(() => collectOfferPerformers(offer, eventOffers), [offer, eventOffers]);

  const poolActive = isPoolPreviewActive(offer, offer?.lineup);
  const artistEarningsOffer = poolActive
    ? activeMemberSettlementDue(offer, calculated?.poolMembers)
    : calculated?.artistEarnings?.artistSettlementDue;
  const artistEarningsForecast = poolActive
    ? activeMemberSettlementDue(offer, calculated?.poolMembersForecast)
    : calculated?.artistEarningsForecast?.artistSettlementDue;
  const artistEarningsActual = poolActive
    ? activeMemberSettlementDue(offer, calculated?.poolMembersActual)
    : calculated?.artistEarningsActual?.artistSettlementDue;

  const {
    showSendModal,
    setShowSendModal,
    sendTo,
    setSendTo,
    sendMessage,
    setSendMessage,
    isSendingOffer,
    handleSendOffer,
    handleSubmitSend,
  } = useV3OfferSend();

  const isMobile = useIsMobile();

  const { eventTemplates, upsertEventTemplate } = useEventTemplates(team.id);
  const [financeCategories, setFinanceCategories] = useState([]);
  const [holdSheetOpen, setHoldSheetOpen] = useState(false);
  const [changeArtistOpen, setChangeArtistOpen] = useState(false);
  const [addArtistOpen, setAddArtistOpen] = useState(false);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);

  // "Change artist" on an existing offer: open the picker from the deal-terms
  // header, then PATCH the primary performer in place. The performer record's
  // id is preserved server-side, so the pane's autosave effect re-syncs off
  // the rotated artistable_id without remounting.
  const openChangeArtist = useCallback(() => setChangeArtistOpen(true), []);
  const handleChangeArtistPick = useCallback(async (picked) => {
    await handleEditPrimaryPerformer(picked);
    setChangeArtistOpen(false);
  }, [handleEditPrimaryPerformer]);

  // "+ Add artist" on the lineup rail: open the shared picker in add-sibling
  // mode, then POST a sibling v3 offer for the picked/created artist.
  const openAddArtist = useCallback(() => setAddArtistOpen(true), []);
  const financeCategoriesFetched = useRef(false);

  // Applying rewrites the primary offer's deal terms and every budget line item,
  // and a multi-artist template recreates the sibling offers outright. The editor
  // caches state derived from the initial mount, so reload rather than try to
  // reconcile — same call the offer-template apply path makes.
  const handleApplyEventTemplate = useCallback(() => {
    window.location.reload();
  }, []);

  // Both are the same operation — see useEventTemplates. Kept as two names
  // because the bar's two dropdowns each report their own outcome.
  const handleEventTemplateCreated = upsertEventTemplate;
  const handleEventTemplateSaved = upsertEventTemplate;

  const handleBudgetPatched = useCallback((payload) => {
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload });
  }, [dispatch]);

  useEffect(() => {
    if (financeCategoriesFetched.current) return undefined;
    financeCategoriesFetched.current = true;

    let ignore = false;
    axios.get(`/teams/${team.id}/finance_categories.json`)
      .then(res => {
        const sorted = (res.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!ignore) setFinanceCategories(sorted);
      })
      .catch(() => { if (!ignore) toastError('Failed to load finance categories. Please try again.'); });
    return () => { ignore = true; };
  }, [team.id]);

  const addFinanceCategory = useCallback((category) => {
    setFinanceCategories(prev => (prev.some(c => c.id === category.id)
      ? prev
      : [...prev, category].sort((a, b) => (a.name || '').localeCompare(b.name || ''))));
  }, []);

  const handleConfirmSaved = useCallback((updatedConfirm) => {
    dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, calendar_event: updatedConfirm } });
  }, [dispatch, offer]);

  const handleReorderLineItems = useCallback((updatedLineItems) => {
    dispatch({ type: V3_ACTIONS.REORDER_LINE_ITEMS, payload: updatedLineItems });
  }, [dispatch]);

  const saveVisibilitySettings = useCallback((settings) => {
    if (!budget?.id) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.patch(`/teams/${team.id}/budgets/v3/${budget.id}`, {
      v3_budget: settings
    }).catch(() => {});
  }, [budget, csrfToken, team]);

  const handleVisibilityChange = useCallback((setting, value) => {
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { [setting]: value } });
    saveVisibilitySettings({ [setting]: value });
  }, [dispatch, saveVisibilitySettings]);

  const handleBulkVisibilityChange = useCallback((columnType, value) => {
    const settingsToUpdate = {};

    if (columnType === 'offer') {
      settingsToUpdate.tickets_show_offer = value;
      settingsToUpdate.other_income_show_offer = value;
      settingsToUpdate.before_adjusted_deductions_show_offer = value;
      settingsToUpdate.additions_show_offer = value;
      settingsToUpdate.expenses_show_offer = value;
      settingsToUpdate.deductions_show_offer = value;
    } else if (columnType === 'forecast') {
      settingsToUpdate.tickets_show_forecast = value;
      settingsToUpdate.other_income_show_forecast = value;
      settingsToUpdate.before_adjusted_deductions_show_forecast = value;
      settingsToUpdate.additions_show_forecast = value;
      settingsToUpdate.ancillary_income_show_forecast = value;
      settingsToUpdate.expenses_show_forecast = value;
      settingsToUpdate.deductions_show_forecast = value;
      settingsToUpdate.ancillary_expenses_show_forecast = value;
    } else if (columnType === 'actuals') {
      settingsToUpdate.tickets_show_actuals = value;
      settingsToUpdate.other_income_show_actuals = value;
      settingsToUpdate.before_adjusted_deductions_show_actuals = value;
      settingsToUpdate.additions_show_actuals = value;
      settingsToUpdate.ancillary_income_show_actuals = value;
      settingsToUpdate.expenses_show_actuals = value;
      settingsToUpdate.deductions_show_actuals = value;
      settingsToUpdate.ancillary_expenses_show_actuals = value;
    }

    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: settingsToUpdate });
    saveVisibilitySettings(settingsToUpdate);
  }, [dispatch, saveVisibilitySettings]);

  const offerActive = useMemo(() => (
    (budget?.tickets_show_offer ?? true) &&
    (budget?.other_income_show_offer ?? true) &&
    (budget?.before_adjusted_deductions_show_offer ?? true) &&
    (budget?.additions_show_offer ?? true) &&
    (budget?.expenses_show_offer ?? true) &&
    (budget?.deductions_show_offer ?? true)
  ), [budget]);

  const forecastActive = useMemo(() => (
    (budget?.tickets_show_forecast ?? true) &&
    (budget?.other_income_show_forecast ?? true) &&
    (budget?.before_adjusted_deductions_show_forecast ?? true) &&
    (budget?.additions_show_forecast ?? true) &&
    (budget?.ancillary_income_show_forecast ?? true) &&
    (budget?.expenses_show_forecast ?? true) &&
    (budget?.deductions_show_forecast ?? true) &&
    (budget?.ancillary_expenses_show_forecast ?? true)
  ), [budget]);

  const actualsActive = useMemo(() => (
    (budget?.tickets_show_actuals ?? true) &&
    (budget?.other_income_show_actuals ?? true) &&
    (budget?.before_adjusted_deductions_show_actuals ?? true) &&
    (budget?.additions_show_actuals ?? true) &&
    (budget?.ancillary_income_show_actuals ?? true) &&
    (budget?.expenses_show_actuals ?? true) &&
    (budget?.deductions_show_actuals ?? true) &&
    (budget?.ancillary_expenses_show_actuals ?? true)
  ), [budget]);

  const originDefaultsApplied = useRef(false);
  useEffect(() => {
    if (originDefaultsApplied.current || !entryOrigin || !budget?.id) return;
    const allDefaults = offerActive && forecastActive && actualsActive;
    if (!allDefaults) return;
    originDefaultsApplied.current = true;

    const localOnly = {};
    const hide = (col) => {
      const prefix = col === 'offer' ? ['tickets', 'other_income', 'before_adjusted_deductions', 'additions', 'expenses', 'deductions']
        : ['tickets', 'other_income', 'before_adjusted_deductions', 'additions', 'ancillary_income', 'expenses', 'deductions', 'ancillary_expenses'];
      prefix.forEach(p => { localOnly[`${p}_show_${col}`] = false; });
    };

    if (entryOrigin === 'offers') { hide('forecast'); hide('actuals'); }
    else if (entryOrigin === 'confirm') { hide('actuals'); }

    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: localOnly });
  }, [entryOrigin, budget, offerActive, forecastActive, actualsActive, dispatch]);

  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [utilityBarHeight, setUtilityBarHeight] = useState(0);

  useEffect(() => {
    setUtilityBarHeight(document.getElementById('utility-bar') ? 40 : 0);
  }, []);

  useEffect(() => {
    const topBar = document.querySelector('.page-top-bar');
    if (topBar) topBar.style.marginBottom = '0';
  }, []);

  // Builds the prop bag the deal-terms pane forwards to V3BudgetBonusManager.
  // The manager edits `budget_bonuses`; we map that onto the V3 budget's
  // `v3_budget_bonuses` association. Create/update/delete persist through the
  // v3_budget_bonuses endpoint (via useV3Budgets) so changes survive into the
  // offer preview and PDF.
  const bonusManagerBudget = useMemo(() => ({
    ...(budget || {}),
    budget_bonuses: budget?.v3_budget_bonuses || [],
  }), [budget]);

  const bonusManagerProps = useMemo(() => ({
    csrfToken,
    team,
    currentUser,
    budget: bonusManagerBudget,
    // Optimistic local update so row edits render immediately; the debounced
    // autoSaveBudgetBonus below persists the change to the server.
    updateBudget: (updated) => {
      dispatch({
        type: V3_ACTIONS.UPDATE_BUDGET,
        payload: { v3_budget_bonuses: updated?.budget_bonuses || [] },
      });
    },
    // Persist on add: POST a blank bonus and append the saved record (with its
    // id) to state. Subsequent field edits autosave via PATCH.
    createBudgetBonus: () => {
      apiCreateBudgetBonus(budget.id);
    },
    deleteBudgetBonus: (_csrf, _t, budgetBonus) => {
      if (budgetBonus.id) {
        apiDeleteBudgetBonus(budgetBonus.id);
      } else {
        dispatch({
          type: V3_ACTIONS.UPDATE_BUDGET,
          payload: {
            v3_budget_bonuses: (budget?.v3_budget_bonuses || []).filter(
              (b) => b._key !== budgetBonus._key
            ),
          },
        });
      }
    },
    autoSaveBudgetBonus: (_csrf, _t, budgetBonus) => {
      if (!budgetBonus.id) return;
      apiUpdateBudgetBonus(budgetBonus.id, {
        ticket_count_requirement: budgetBonus.ticket_count_requirement
          ? parseFloatFromString(budgetBonus.ticket_count_requirement)
          : '',
        dollar_or_percentage_operator: budgetBonus.dollar_or_percentage_operator,
        dollar_amount: budgetBonus.dollar_amount
          ? parseFloatFromString(budgetBonus.dollar_amount)
          : '',
        upside_percentage: budgetBonus.upside_percentage
          ? parseFloatFromString(budgetBonus.upside_percentage)
          : '',
      });
    },
    // Source brackets from the budget (context's dealBrackets slice is unused
    // and always empty); the bonus row needs the real list to relate bonuses
    // to deal brackets.
    dealBrackets: budget?.v3_deal_brackets || [],
  }), [csrfToken, team, currentUser, bonusManagerBudget, budget, dispatch, apiCreateBudgetBonus, apiUpdateBudgetBonus, apiDeleteBudgetBonus]);

  const handleColumnToggle = useCallback((columnType) => {
    const currentActive =
      columnType === 'offer' ? offerActive :
      columnType === 'forecast' ? forecastActive :
      actualsActive;
    handleBulkVisibilityChange(columnType, !currentActive);
  }, [offerActive, forecastActive, actualsActive, handleBulkVisibilityChange]);

  const [linkOfferModalOpen, setLinkOfferModalOpen] = useState(false);
  const [proFormaOpen, setProFormaOpen] = useState(false);
  const [offerContacts, setOfferContacts] = useState([]);

  // Lineup-rail action handlers (offer-switch, reorder, inline new-artist
  // scaffold). The hook owns the refs and effects backing these — the page
  // just wires the returned callbacks into V3OfferLineupRail.
  const {
    handleSelectOffer,
    handleReorder,
    handlePickNewArtist,
  } = useV3LineupActions();

  const handleAddArtistPick = useCallback(async (picked) => {
    const added = await handlePickNewArtist(picked);
    if (added) setAddArtistOpen(false);
  }, [handlePickNewArtist]);

  // Seed contacts whenever the active offer changes.
  useEffect(() => {
    setOfferContacts(offer?.v3_offer_contacts || offer?.offer_contacts || []);
  }, [offer?.id]);

  // Mirrors V3OfferManageContacts.addOfferContact so the deal-terms-header's
  // "+ Add contacts" link can open an inline new-contact row without reaching
  // into the contacts component. Appends an unsaved placeholder; the contacts
  // section renders the inline form when an entry without an id is present.
  const handleAddContact = useCallback(() => {
    setOfferContacts((current) => {
      // Prevent duplicate unsaved placeholders.
      if (current.some((oc) => oc.id === undefined)) return current;
      const cleaned = current.filter((oc) => oc.id !== undefined);
      return [
        ...cleaned,
        {
          id: undefined,
          contact: {
            name: '',
            email: '',
            phone: '',
            contact_type: '',
            title: '',
            company_name: '',
          },
        },
      ];
    });
  }, []);

  const [settlementTickets, setSettlementTickets] = useState([]);
  const [addOnActuals, setAddOnActuals] = useState({});

  const fetchSettlementData = useCallback((isIgnored) => {
    if (!offer?.id || !offer?.calendar_event_id) {
      if (isIgnored && isIgnored()) return;
      setSettlementTickets([]);
      setAddOnActuals({});
      return;
    }
    axios.get(`/teams/${team.id}/offers/v3/${offer.id}/settlement.json`)
      .then(({ data }) => {
        if (isIgnored && isIgnored()) return;
        const settlementTicketItems = data.settlement_tickets || [];
        setSettlementTickets(settlementTicketItems);

        const addOnItems = data.add_on_actuals || {};
        setAddOnActuals(addOnItems);

        let mergedBudget = { ...budgetForComponents };
        let needsUpdate = false;

        if (settlementTicketItems.length > 0 && budgetForComponents?.ticket_line_items) {
          const matchedSettlementIds = new Set();
          const baseTicketLineItems = budgetForComponents.ticket_line_items.filter(
            t => !t.isFromSettlement
          );

          const mergedTicketLineItems = baseTicketLineItems.map(budgetTicket => {
            const matchingSettlement = settlementTicketItems.find(
              st => st.name === budgetTicket.name &&
                    parseFloat(st.left_hand) === parseFloat(budgetTicket.left_hand)
            );
            if (matchingSettlement) {
              matchedSettlementIds.add(matchingSettlement.uuid || matchingSettlement.id);
              return {
                ...budgetTicket,
                left_hand_actual: matchingSettlement.left_hand,
                right_hand_actual: matchingSettlement.right_hand,
                // O-4509: left_hand_actual is the ALL-IN price for teams whose
                // settlement price includes tax, so the engine needs the tax
                // that is sitting inside it.
                taxes: matchingSettlement.taxes,
                tax_rate: matchingSettlement.tax_rate
              };
            }
            return {
              ...budgetTicket,
              left_hand_actual: budgetTicket.left_hand_actual || budgetTicket.left_hand,
              right_hand_actual: budgetTicket.right_hand_actual || 0
            };
          });

          const settlementOnlyTickets = settlementTicketItems
            .filter(st => !matchedSettlementIds.has(st.uuid || st.id))
            .map(st => ({
              uuid: st.uuid || `settlement-${st.id}`,
              name: st.name,
              category: 'TICKET',
              left_hand: st.left_hand,
              left_hand_forecast: "0",
              left_hand_actual: st.left_hand,
              right_hand: 0,
              right_hand_forecast: "0",
              right_hand_actual: st.right_hand,
              operator: '*',
              calc_type: 'fixed',
              show_in_offer: true,
              taxes: st.taxes,
              tax_rate: st.tax_rate,
              isFromSettlement: true
            }));

          mergedBudget.ticket_line_items = [...mergedTicketLineItems, ...settlementOnlyTickets];
          needsUpdate = true;
        }

        if (Object.keys(addOnItems).length > 0 && budgetForComponents?.income_line_items) {
          const mergedIncomeLineItems = budgetForComponents.income_line_items.map(item => {
            const actualsForItem = addOnItems[item.name];
            if (actualsForItem) {
              return {
                ...item,
                left_hand_actual: actualsForItem.left_hand_actual,
                right_hand_actual: actualsForItem.right_hand_actual
              };
            }
            return item;
          });

          mergedBudget.income_line_items = mergedIncomeLineItems;
          needsUpdate = true;
        }

        if (needsUpdate) {
          handleUpdateBudget(mergedBudget);
        }
      })
      .catch(() => {
        if (isIgnored && isIgnored()) return;
        setSettlementTickets([]);
        setAddOnActuals({});
      });
  }, [offer?.id, offer?.calendar_event_id, team?.id, budgetForComponents, handleUpdateBudget]);

  useEffect(() => {
    let ignore = false;
    fetchSettlementData(() => ignore);
    return () => { ignore = true; };
  }, [offer?.id, offer?.calendar_event_id]);

  return {
    csrfToken,
    team,
    currentUser,
    offer,
    performers,
    budget,
    lineItems,
    isSaving,
    eventOffers,
    confirm,
    entryOrigin,
    changelogVersions,
    lastEditedAt,
    dispatch,
    handlePreview,
    handleCopy,
    copyModalOpen,
    isCopying,
    handleSubmitCopy,
    handleCancelCopy,
    handleDownload,
    handleDelete,
    handleChangeActType,
    handleCreateHold,
    canCopyHolds,
    handleCopyHolds,
    handleEditPrimaryPerformer,
    updateOffer: updateOfferRecordingVariant,
    handlePassThroughChange,
    reorderLineItem,
    budgetForComponents,
    siblingGuaranteeExpenses,
    budgetForMath,
    calculated,
    eventVenueNet,
    hasSecondaryCurrencySymbol,
    globalCurrency,
    showCurrencyConversion,
    handleUpdateBudget: handleUpdateBudgetRecordingVariant,
    handleChangeModifierField,
    refreshBudgetChildren,
    flushModifierFields,
    handleAutoSaveLineItem: handleAutoSaveLineItemRecordingVariant,
    handleDeleteLineItem,
    handleCreateTicketLineItem,
    handleCreateIncomeLineItem,
    handleCreateAdditionLineItem,
    handleCreateExpenseLineItem,
    handleCreateDeductionLineItem,
    handleCreateBeforeAdjustedDeductionsLineItem,
    handleCreateAncillaryIncomeLineItem,
    handleCreateAncillaryExpenseLineItem,
    formatCalculatedNumber,
    poolActive,
    artistEarningsOffer,
    artistEarningsForecast,
    artistEarningsActual,
    showSendModal,
    setShowSendModal,
    sendTo,
    setSendTo,
    sendMessage,
    setSendMessage,
    isSendingOffer,
    handleSendOffer,
    handleSubmitSend,
    handleSelectOffer,
    handleReorder,
    handlePickNewArtist,
    offerActive,
    forecastActive,
    actualsActive,
    handleColumnToggle,
    handleVisibilityChange,
    handleReorderLineItems,
    bonusManagerProps,
    isMobile,
    summaryExpanded,
    setSummaryExpanded,
    utilityBarHeight,
    eventTemplates,
    handleApplyEventTemplate,
    handleEventTemplateCreated,
    handleEventTemplateSaved,
    handleBudgetPatched,
    financeCategories,
    addFinanceCategory,
    offerContacts,
    setOfferContacts,
    handleAddContact,
    settlementTickets,
    addOnActuals,
    linkOfferModalOpen,
    setLinkOfferModalOpen,
    proFormaOpen,
    setProFormaOpen,
    holdSheetOpen,
    setHoldSheetOpen,
    changeArtistOpen,
    setChangeArtistOpen,
    addArtistOpen,
    setAddArtistOpen,
    createConfirmOpen,
    setCreateConfirmOpen,
    openChangeArtist,
    handleChangeArtistPick,
    openAddArtist,
    handleAddArtistPick,
    handleConfirmSaved,
  };
}
